import { CustomerDetailsPayload, SimulatorConfigPayload } from '../../../interfaces/customer-simulator';

import { OpenAIHost, VerticalTemplateOption } from '../../../constants/common';
import { accountDetailsRequestHandler, addAccountRequestHandler } from '../../Utilities/ApiRequestHandler';

interface SimulatorFormData {
  accountName: string;
  apiKey: string;
  isCustomerSimulator: boolean;
  host?: OpenAIHost;
  clientId?: string;
  azureResource?: string;
  selectedModel?: string;
  apiVersion?: string;
  selectedVerticalOption?: VerticalTemplateOption;
}

export async function addNewAISimulatorConnection(sessionId: string, AISimulatorPayload: SimulatorFormData) {
  if (!sessionId || sessionId === '') return;

  const requestOptions = {
    body: JSON.stringify(AISimulatorPayload),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'x-session-id': sessionId,
    },
    method: 'POST',
  };
  return await addAccountRequestHandler(`${process.env.REACT_APP_API_PATH}/connections/custom`, requestOptions);
}

export async function updateCustomerAiSimulatorConnection(
  sessionId: string,
  accountId: string,
  AISimulatorPayload: SimulatorConfigPayload | CustomerDetailsPayload,
) {
  if (!sessionId || sessionId === '' || !accountId || accountId === '') return;
  return await fetch(`${process.env.REACT_APP_CUSTOMER_AI_SIMULATOR_WEBHOOK}/update-simulation-status/${accountId}`, {
    headers: {
      'x-session-id': sessionId,
    },
    method: 'POST',
    body: JSON.stringify(AISimulatorPayload),
  })
    .then(async (response) => {
      if (response.status === 200) {
        const json = await response.json();
        return {
          data: json,
          status: 200,
        };
      } else {
        const json = await response.json();
        if (json?.notValidConnection) {
          return {
            errorMessage: json?.reason || json?.errorMessage,
            status: json?.statusCode,
            notValidConnection: json?.notValidConnection,
          };
        } else {
          if (response.status === 403) {
            return {
              errorMessage: 'invalid_session',
              status: response.status,
            };
          } else {
            return {
              errorMessage: 'failed_to_update_simulator_configuration',
              status: response.status,
            };
          }
        }
      }
    })
    .catch((error) => {
      console.error('Error:', error);
      return null;
    });
}

export async function updateCustomSimulator(sessionId: string, accountId: string, payload: any) {
  if (!sessionId || sessionId === '' || !accountId || accountId === '') return;
  return await accountDetailsRequestHandler(
    `${process.env.REACT_APP_API_PATH}/connections/custom/${accountId}`,
    {
      headers: {
        'x-session-id': sessionId,
      },
      method: 'POST',
      body: JSON.stringify(payload),
    },
    'failed_to_update_connection',
  );
}

export async function getSimulationDetails(sessionId: string, connectionId: string) {
  if (!sessionId || sessionId === '' || !connectionId || connectionId === '') return;
  return await accountDetailsRequestHandler(
    `${process.env.REACT_APP_CUSTOMER_AI_SIMULATOR_WEBHOOK}/get-simulation-records/${connectionId}`,
    {
      headers: {
        'x-session-id': sessionId,
      },
    },
    '',
  );
}

export async function genAiUpdatePersona(sessionId: string, accountId: string, payload: any, partial: string) {
  if (!sessionId || sessionId === '' || !accountId || accountId === '') return;
  return await accountDetailsRequestHandler(
    `${process.env.REACT_APP_CUSTOMER_AI_SIMULATOR_WEBHOOK}/gen-ai-partial/${accountId}/${partial}`,
    {
      headers: {
        'x-session-id': sessionId,
      },
      method: 'POST',
      body: JSON.stringify(payload),
    },
    '',
  );
}

export async function genAiUpdateFeedbackData(sessionId: string, accountId: string, payload: any, partial: string) {
  if (!sessionId || sessionId === '' || !accountId || accountId === '') return;
  return await accountDetailsRequestHandler(
    `${process.env.REACT_APP_CUSTOMER_AI_SIMULATOR_WEBHOOK}/gen-ai-partial/${accountId}/${partial}`,
    {
      headers: {
        'x-session-id': sessionId,
      },
      method: 'POST',
      body: JSON.stringify(payload),
    },
    '',
  );
}

export async function getAzureModels(sessionId: string, payload: any) {
  if (!sessionId || sessionId === '') return;
  const updatedPayload = { host: OpenAIHost.Azure, ...payload };
  return await fetch(`${process.env.REACT_APP_CUSTOMER_AI_SIMULATOR_WEBHOOK}/get-azure-models`, {
    headers: {
      'x-session-id': sessionId,
    },
    method: 'POST',
    body: JSON.stringify(updatedPayload),
  })
    .then(async (response) => {
      if (response.status === 200) {
        const json = await response?.json();
        return {
          data: json,
          status: 200,
        };
      }
    })
    .catch((error) => {
      console.error('Error:', error);
      return null;
    });
}
